import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

const tenantId = process.env.REACT_APP_AZURE_AD_TENANT_ID;
const clientId = process.env.REACT_APP_AZURE_AD_CLIENT_ID;
const LOCAL_RUNTIME_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

const isMsalConfigured = () => {
  return Boolean(tenantId && clientId);
};

const resolveUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  try {
    return new URL(value.trim(), window.location.origin).toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
};

const isLocalDevelopmentRuntime = () => {
  if (typeof window === 'undefined') return false;
  const hostname = (window.location.hostname || '').toLowerCase();
  return process.env.NODE_ENV === 'development' || LOCAL_RUNTIME_HOSTS.has(hostname);
};

const shouldPreferRuntimeOrigin = (resolvedUrl) => {
  if (!resolvedUrl || typeof window === 'undefined') return false;
  if (!isLocalDevelopmentRuntime()) return false;

  try {
    const runtimeOrigin = window.location.origin.replace(/\/+$/, '');
    const resolvedOrigin = new URL(resolvedUrl).origin.replace(/\/+$/, '');
    return resolvedOrigin !== runtimeOrigin;
  } catch {
    return false;
  }
};

const getConfiguredRedirectUri = () => {
  const configuredRedirectUri = process.env.REACT_APP_AZURE_AD_REDIRECT_URI;
  if (configuredRedirectUri) {
    const resolved = resolveUrl(configuredRedirectUri);
    if (resolved) {
      if (shouldPreferRuntimeOrigin(resolved)) {
        console.warn(
          `MSAL: Ignoring configured redirect URI '${resolved}' during local dev; using current origin '${window.location.origin}' instead.`,
        );
        return window.location.origin;
      }
      return resolved;
    }
    console.warn('MSAL: Invalid REACT_APP_AZURE_AD_REDIRECT_URI, falling back to current origin');
  }

  return window.location.origin;
};

const getConfiguredPostLogoutRedirectUri = (resolvedRedirectUri) => {
  const configuredPostLogoutUri = process.env.REACT_APP_AZURE_AD_POST_LOGOUT_REDIRECT_URI;
  if (configuredPostLogoutUri) {
    const resolved = resolveUrl(configuredPostLogoutUri);
    if (resolved) {
      if (shouldPreferRuntimeOrigin(resolved)) {
        console.warn(
          `MSAL: Ignoring configured post-logout redirect URI '${resolved}' during local dev; using '${window.location.origin}/login' instead.`,
        );
        return `${window.location.origin}/login`;
      }
      return resolved;
    }
    console.warn('MSAL: Invalid REACT_APP_AZURE_AD_POST_LOGOUT_REDIRECT_URI, falling back to /login');
  }

  try {
    const redirectBase = new URL(resolvedRedirectUri);
    return `${redirectBase.origin}/login`;
  } catch {
    return `${window.location.origin}/login`;
  }
};

const redirectUri = getConfiguredRedirectUri();
const postLogoutRedirectUri = getConfiguredPostLogoutRedirectUri(redirectUri);

const msalConfig = isMsalConfigured()
  ? {
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: true, // Enable for better cross-tab SSO
    },
    system: {
      // Reduce iframe timeout to fail faster (default is 10s)
      // This prevents long waits when third-party cookies are blocked
      iframeHashTimeout: 6000,
      windowHashTimeout: 6000,
      loadFrameTimeout: 6000,
      // Don't log verbose messages in production
      loggerOptions: {
        logLevel: process.env.NODE_ENV === 'production' ? 0 : 2, // 0=Error, 2=Verbose
      },
    },
  }
  : {
    auth: {
      clientId: 'missing',
      authority: 'https://login.microsoftonline.com/common',
      redirectUri,
    },
  };

// Create a single shared instance.
const msalInstance = new PublicClientApplication(msalConfig);

// Track initialization state
let msalInitialized = false;
let msalInitAttempts = 0;
const MAX_INIT_ATTEMPTS = 3;
let msalInitPromise = null;
const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status';

const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
};

const normalizeRoleValue = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim().toLowerCase();
  const exactMatches = {
    admin: 'admin',
    administrator: 'admin',
    estimator: 'estimator',
  };

  if (exactMatches[normalized]) return exactMatches[normalized];

  const qualifiedSegments = normalized.split(/[.:/_\s]+/).filter(Boolean);
  const trailingSegment = qualifiedSegments[qualifiedSegments.length - 1];
  if (trailingSegment && exactMatches[trailingSegment]) {
    return exactMatches[trailingSegment];
  }

  return null;
};

const resolveRoleFromClaims = (claims = {}) => {
  const rawRoles = [
    claims.roles,
    claims.role,
    claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
  ].filter(Boolean);

  const flattened = rawRoles.flatMap((value) =>
    Array.isArray(value) ? value : [value]
  );

  for (const value of flattened) {
    const normalizedRole = normalizeRoleValue(value);
    if (normalizedRole) return normalizedRole;
  }

  return null;
};

const getInteractionStatusValue = () => {
  if (typeof window === 'undefined') return null;

  if (typeof document !== 'undefined') {
    const cookiePrefix = `${MSAL_INTERACTION_STATUS_KEY}=`;
    const cookieValue = document.cookie
      .split(';')
      .map((entry) => entry.trim())
      .find((entry) => entry.startsWith(cookiePrefix));

    if (cookieValue) {
      return cookieValue.slice(cookiePrefix.length);
    }
  }

  const storages = [window.sessionStorage, window.localStorage].filter(Boolean);
  for (const storage of storages) {
    try {
      const value = storage.getItem(MSAL_INTERACTION_STATUS_KEY);
      if (value) return value;
    } catch (error) {
      console.warn('MSAL: Unable to read interaction status from storage', error);
    }
  }

  return null;
};

const hasAuthResponseInUrl = () => {
  if (typeof window === 'undefined') return false;

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const authKeys = ['code', 'error', 'state', 'id_token'];

  return authKeys.some((key) => searchParams.has(key) || hashParams.has(key));
};

const clearMsalInteractionStatus = () => {
  if (typeof window === 'undefined') return;

  const storages = [window.sessionStorage, window.localStorage].filter(Boolean);
  for (const storage of storages) {
    try {
      storage.removeItem(MSAL_INTERACTION_STATUS_KEY);
    } catch (error) {
      console.warn('MSAL: Unable to clear interaction status from storage', error);
    }
  }

  if (typeof document !== 'undefined') {
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const cookieVariants = [
      `${MSAL_INTERACTION_STATUS_KEY}=; expires=${expires}; path=/`,
      `${MSAL_INTERACTION_STATUS_KEY}=; expires=${expires}; path=${window.location.pathname || '/'}`,
    ];

    cookieVariants.forEach((cookieValue) => {
      try {
        document.cookie = cookieValue;
      } catch (error) {
        console.warn('MSAL: Unable to clear interaction status cookie', error);
      }
    });
  }
};

const clearStaleMsalInteractionStatus = (reason) => {
  const interactionStatus = getInteractionStatusValue();
  if (!interactionStatus || hasAuthResponseInUrl()) {
    return false;
  }

  console.warn(`MSAL: Clearing stale interaction status (${reason})`);
  clearMsalInteractionStatus();
  return true;
};

/**
 * Initialize MSAL and handle any redirect responses.
 * Must be called before using other MSAL functions.
 * Retries on transient errors up to MAX_INIT_ATTEMPTS times.
 */
const initializeMsal = async () => {
  if (msalInitialized) return;
  if (msalInitPromise) return msalInitPromise;
  if (!isMsalConfigured()) {
    console.log('MSAL: Not configured, skipping initialization');
    return;
  }

  // Prevent infinite retry loops
  if (msalInitAttempts >= MAX_INIT_ATTEMPTS) {
    console.warn(`MSAL: Max initialization attempts (${MAX_INIT_ATTEMPTS}) reached, giving up`);
    return;
  }

  msalInitAttempts++;
  msalInitPromise = (async () => {
    try {
      // MSAL v3+ requires explicit initialization before any API calls
      console.log('MSAL: Calling msalInstance.initialize()...');
      await msalInstance.initialize();
      console.log('MSAL: Instance initialized');

      // Handle redirect response if returning from login
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        console.log('MSAL: Redirect response received', response.account?.username);
        msalInstance.setActiveAccount(response.account);
      } else {
        clearStaleMsalInteractionStatus('no redirect response after initialization');
      }
      msalInitialized = true;
      console.log('MSAL: Initialized successfully');
    } catch (error) {
      console.error(`MSAL: Initialization error (attempt ${msalInitAttempts}/${MAX_INIT_ATTEMPTS})`, error);

      // Only mark as permanently initialized for non-transient errors
      const isTransientError = error.name === 'BrowserAuthError' &&
        (error.errorCode === 'monitor_window_timeout' ||
          error.errorCode === 'network_error' ||
          error.message?.includes('network'));

      if (!isTransientError) {
        // Permanent error (config issue, etc.) - don't retry
        msalInitialized = true;
        console.warn('MSAL: Permanent initialization error, will not retry');
      }
      // For transient errors, leave msalInitialized false so retry is possible
    } finally {
      msalInitPromise = null;
    }
  })();

  return msalInitPromise;
};

/**
 * Extract login_hint from URL query parameters.
 * Island Platform passes this when redirecting to CostPilot.
 */
const getLoginHintFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const loginHint = params.get('login_hint');
  if (loginHint) {
    console.log('MSAL SSO: Found login_hint in URL:', loginHint);
  }
  return loginHint;
};

/**
 * Attempt to silently acquire a token using SSO.
 * This works if the user has an active session with Azure AD (e.g., from Island Platform).
 * Returns the account info if successful, null otherwise.
 * 
 * IMPORTANT: We only attempt iframe-based ssoSilent() if:
 * 1. There's a cached account in localStorage (same-origin, fast)
 * 2. There's a login_hint from the parent platform (cross-origin SSO)
 * 
 * Without these, ssoSilent() will create a hidden iframe that will timeout
 * due to third-party cookie restrictions in modern browsers.
 */
const trySilentSso = async () => {
  if (!isMsalConfigured()) {
    console.log('MSAL SSO: Not configured');
    return null;
  }

  await initializeMsal();

  // Parse login_hint from URL if present (passed from Island Platform)
  const loginHint = getLoginHintFromUrl();

  try {
    // First check if we already have an account in cache (same-origin, no iframe needed)
    const accounts = msalInstance.getAllAccounts();
    console.log(`MSAL SSO: Found ${accounts.length} cached account(s)`);

    if (accounts.length > 0) {
      console.log('MSAL SSO: Found cached account', accounts[0].username);
      msalInstance.setActiveAccount(accounts[0]);

      // Silently acquire token to ensure session is still valid
      const silentRequest = {
        ...loginRequest,
        account: accounts[0],
      };

      // Add loginHint if available to help with account matching
      if (loginHint) {
        silentRequest.loginHint = loginHint;
      }

      const silentResult = await msalInstance.acquireTokenSilent(silentRequest);
      console.log('MSAL SSO: Token acquired silently for cached account');

      return formatMsalUser(silentResult);
    }

    // No cached account - only try ssoSilent if we have a login_hint from the platform
    // Without login_hint, ssoSilent will create an iframe that will timeout anyway
    if (!loginHint) {
      console.log('MSAL SSO: No cached account and no login_hint - skipping iframe-based SSO');
      console.log('MSAL SSO: User will need to click "Sign in with Microsoft" or navigate from Island Platform with login_hint');
      return null;
    }

    // We have a login_hint - try ssoSilent to pick up the session from parent platform
    console.log('MSAL SSO: Attempting silent SSO with login_hint:', loginHint);

    const ssoRequest = {
      ...loginRequest,
      loginHint,
    };

    const ssoResult = await msalInstance.ssoSilent(ssoRequest);

    if (ssoResult?.account) {
      console.log('MSAL SSO: Silent SSO successful!', ssoResult.account.username);
      msalInstance.setActiveAccount(ssoResult.account);
      return formatMsalUser(ssoResult);
    }

    console.log('MSAL SSO: ssoSilent returned no account');
    return null;
  } catch (error) {
    // Handle specific error types
    const errorCode = error.errorCode || error.name;

    // Timeout errors - iframe-based silent auth failed (common with third-party cookie blocking)
    if (errorCode === 'monitor_window_timeout' || errorCode === 'hash_empty_error') {
      console.log('MSAL SSO: Silent auth timed out (likely due to third-party cookie restrictions)');
      console.log('MSAL SSO: Users will need to click "Sign in with Microsoft" for explicit login');
      return null;
    }

    // Interaction required - user needs to explicitly sign in
    if (error instanceof InteractionRequiredAuthError) {
      console.log('MSAL SSO: Interaction required - no active session found');
      console.log('MSAL SSO: Error details:', error.errorCode, error.errorMessage);
      // Don't attempt automatic redirect - let user click login button
      // The prompt:'none' redirect causes iframe sandbox issues
      return null;
    }

    // Other errors
    console.warn('MSAL SSO: Silent SSO failed:', errorCode, error.errorMessage || error.message);
    return null;
  }
};

/**
 * Format MSAL authentication result into our user object format.
 */
const formatMsalUser = (authResult) => {
  if (!authResult?.account) return null;

  const account = authResult.account;
  const claims = authResult.idTokenClaims || {};
  const resolvedRole = resolveRoleFromClaims(claims);
  const isAdmin = resolvedRole === 'admin';

  return {
    id: account.localAccountId || account.homeAccountId,
    email: account.username || claims.email || claims.preferred_username,
    name: account.name || claims.name || account.username,
    firstName: claims.given_name || '',
    lastName: claims.family_name || '',
    provider: 'msal',
    provider_id: 'azure_ad',
    access_token: authResult.accessToken,
    id_token: authResult.idToken,
    claims,
    role: resolvedRole || 'none',
    is_admin: isAdmin,
  };
};

const getActiveAccount = () => {
  if (!isMsalConfigured()) return null;
  return msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0] || null;
};

/**
 * Exchange MSAL ID token for app JWT tokens by calling backend.
 * Returns user data and tokens if successful, null otherwise.
 */
const exchangeTokenForJwt = async () => {
  if (!isMsalConfigured()) {
    console.log('MSAL: Not configured, skipping token exchange');
    return null;
  }

  try {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      console.log('MSAL: No active account, cannot exchange token');
      return null;
    }

    // Get the ID token from MSAL
    const tokenRequest = {
      ...loginRequest,
      account,
    };

    const response = await msalInstance.acquireTokenSilent(tokenRequest);

    if (!response.idToken) {
      console.warn('MSAL: No ID token in response');
      return null;
    }

    console.log('MSAL: Exchanging ID token for JWT...');

    // Call backend to exchange MSAL token for JWT
    const jwtResponse = await fetch('/api/auth/aad-login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id_token: response.idToken,
      }),
    });

    if (!jwtResponse.ok) {
      const error = await jwtResponse.json().catch(() => ({}));
      console.error('MSAL: Token exchange failed:', error);
      // Log audience mismatch details if available
      if (error.actual_audience && error.expected_audiences) {
        console.error('MSAL: Token audience mismatch!');
        console.error('  Expected:', error.expected_audiences);
        console.error('  Actual:', error.actual_audience);
        console.error('  Fix:', error.fix);
      }
      // Log other error details (database errors, etc.)
      if (error.error_type) {
        console.error('MSAL: Error type:', error.error_type);
        console.error('MSAL: Details:', error.details || error.message);
      }
      return null;
    }

    const tokenData = await jwtResponse.json();
    console.log('MSAL: Token exchange successful, user:', tokenData.user?.email);

    // Store JWT tokens in localStorage for apiService to use
    localStorage.setItem('accessToken', tokenData.access_token);
    localStorage.setItem('refreshToken', tokenData.refresh_token);
    localStorage.setItem('user', JSON.stringify(tokenData.user));

    return tokenData.user;
  } catch (error) {
    console.error('MSAL: Error during token exchange:', error);
    return null;
  }
};

/**
 * Exchange a provided Azure AD id_token (e.g., from a parent platform) for app JWTs.
 */
const exchangeExternalIdTokenForJwt = async (idToken) => {
  if (!idToken) return null;
  if (!isMsalConfigured()) {
    console.log('MSAL: Not configured, skipping external token exchange');
    return null;
  }

  try {
    console.log('MSAL: Exchanging external id_token for JWT...');
    const jwtResponse = await fetch('/api/auth/aad-login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id_token: idToken,
      }),
    });

    if (!jwtResponse.ok) {
      const errorData = await jwtResponse.json().catch(() => ({}));
      console.error('MSAL: External token exchange failed:', errorData);
      // Log audience mismatch details if available
      if (errorData.actual_audience && errorData.expected_audiences) {
        console.error('MSAL: Token audience mismatch!');
        console.error('  Expected:', errorData.expected_audiences);
        console.error('  Actual:', errorData.actual_audience);
        console.error('  Fix:', errorData.fix);
      }
      // Log other error details (database errors, etc.)
      if (errorData.error_type) {
        console.error('MSAL: Error type:', errorData.error_type);
        console.error('MSAL: Details:', errorData.details || errorData.message);
      }
      return null;
    }

    const tokenData = await jwtResponse.json();
    console.log('MSAL: External token exchange successful, user:', tokenData.user?.email);
    localStorage.setItem('accessToken', tokenData.access_token);
    localStorage.setItem('refreshToken', tokenData.refresh_token);
    localStorage.setItem('user', JSON.stringify(tokenData.user));
    return tokenData.user;
  } catch (error) {
    console.error('MSAL: Error during external token exchange:', error);
    return null;
  }
};

/**
 * Fetch user's profile photo from Microsoft Graph.
 * Returns a base64 data URL or null if unavailable.
 */
const getMicrosoftPhoto = async () => {
  if (!isMsalConfigured()) return null;

  try {
    await initializeMsal();
    const account = msalInstance.getActiveAccount();
    if (!account) return null;

    // Request token with Graph scope for user photo
    const tokenRequest = {
      scopes: ['User.Read'],
      account,
    };

    const response = await msalInstance.acquireTokenSilent(tokenRequest);

    if (!response.accessToken) return null;

    // Fetch photo from Microsoft Graph
    const photoResponse = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: {
        Authorization: `Bearer ${response.accessToken}`,
      },
    });

    if (!photoResponse.ok) {
      console.log('MSAL: No profile photo available (this is normal for new accounts)');
      return null;
    }

    // Convert blob to base64 data URL
    const blob = await photoResponse.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.log('MSAL: Could not fetch profile photo:', error.message);
    return null;
  }
};

const loginWithMicrosoftRedirect = async () => {
  if (!isMsalConfigured()) {
    throw new Error('Microsoft login is not configured (missing REACT_APP_AZURE_AD_CLIENT_ID / REACT_APP_AZURE_AD_TENANT_ID)');
  }
  await initializeMsal();

  if (clearStaleMsalInteractionStatus('before login redirect')) {
    await initializeMsal();
  }

  const interactionStatus = getInteractionStatusValue();
  if (interactionStatus) {
    throw new Error('Microsoft sign-in is still finishing. Refresh the page once and try again.');
  }

  try {
    return await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    if (error?.errorCode === 'interaction_in_progress' && clearStaleMsalInteractionStatus('recovering from login redirect error')) {
      return msalInstance.loginRedirect(loginRequest);
    }

    throw error;
  }
};

const logoutMicrosoftRedirect = async () => {
  if (!isMsalConfigured()) return;
  await initializeMsal();
  const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];
  return msalInstance.logoutRedirect({
    account,
    postLogoutRedirectUri,
  });
};

export {
  msalInstance,
  loginRequest,
  isMsalConfigured,
  initializeMsal,
  trySilentSso,
  formatMsalUser,
  getActiveAccount,
  getLoginHintFromUrl,
  exchangeTokenForJwt,
  exchangeExternalIdTokenForJwt,
  getMicrosoftPhoto,
  loginWithMicrosoftRedirect,
  logoutMicrosoftRedirect,
};
